import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import {
  useCreatePlanMutation,
  useGetPlansQuery,
  useGetPlanByIdQuery,
  useEditPlanMutation,
} from "../../services/subscription";
import {
  useGetSegmentsQuery,
  useGetTiersQuery,
} from "../../services/subscriberManagement"; // Adjust path if needed
import { useGetTaxesQuery } from "../../services/tax/tax.rtk.api";
import { toast } from "../../core/toast";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { adminRoutes } from "../../routers/AdminRouters";
import { ISegment, ITier } from "../../types";
import { Plan } from "../../types/subscription/subscriptionScreen/subscriptionScreen.interface";
import { ScreenType } from "../../types/subscription/subscriptionScreen/subscriptionScreen.type";

interface PlanPrice {
  id: string; // internal temp id
  currency: string;
  price: number;
  frequency: "Monthly" | "Annual";
}

const PlanCreate: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id: planId } = useParams<{ id: string }>();

  const [createPlan, { isLoading: isCreating }] = useCreatePlanMutation();
  const [editPlan, { isLoading: isUpdating }] = useEditPlanMutation();
  const { data: planDetail, isLoading: isPlanLoading } = useGetPlanByIdQuery(
    planId as string,
    {
      skip: !planId,
    },
  );

  const isLoading = isCreating || isUpdating;

  // Data for Dropdowns
  const { data: segmentsData } = useGetSegmentsQuery();
  const { data: tiersData } = useGetTiersQuery();
  const { data: plansData } = useGetPlansQuery();

  const segments: ISegment[] = segmentsData || [];
  const tiers: ITier[] = tiersData || [];
  const plans: Plan[] = plansData?.results || plansData?.data?.results || [];

  // Form State
  const [activeTab, setActiveTab] = useState<"License" | "Addon">("License");
  const [formData, setFormData] = useState({
    name: "",
    segment: "",
    tier: "",
    planType: "", // Basic | Premium
    parentPlan: "", // For Addon
    noScreens: 2,
    description: "",
  });

  // Handle Tab Change Defaults
  React.useEffect(() => {
    if (activeTab === "License") {
      setFormData((prev) => ({
        ...prev,
        noScreens: 2,
        parentPlan: "",
        segment: "",
        tier: "",
        planType: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        noScreens: 1,
        name: "",
      }));
    }
  }, [activeTab]);

  // Price List State
  const [priceForm, setPriceForm] = useState<{
    currency: string;
    price: number;
    frequency: "Monthly" | "Annual";
  }>({
    currency: "INR",
    price: 0,
    frequency: "Monthly",
  });
  const [priceList, setPriceList] = useState<PlanPrice[]>([]);

  // Hydrate form when editing
  useEffect(() => {
    if (!planDetail?.result) return;
    const plan: any = planDetail.result;

    setFormData({
      name: plan?.name ?? "",
      segment: plan?.segment?._id || plan?.segment || "",
      tier: plan?.tier?._id || plan?.tier || "",
      planType: plan?.type || "",
      parentPlan: plan?.basePlan || "",
      noScreens: plan?.screens ?? 2,
      description: plan?.description ?? "",
    });

    // Set active tab based on variant
    if (plan?.variant === "ADDON_SCREEN") {
      setActiveTab("Addon");
    } else {
      setActiveTab("License");
    }

    // Hydrate price list from plan prices
    if (plan?.prices && Array.isArray(plan.prices)) {
      const frequency =
        plan?.cycle === "MONTHLY" ? "Monthly" : "Annual";
      const hydratedPrices: PlanPrice[] = plan.prices.map(
        (p: any, index: number) => ({
          id: `${Date.now()}-${index}`,
          currency: p.currency || "INR",
          price: p.price || 0,
          frequency: frequency,
        }),
      );
      setPriceList(hydratedPrices);
      setPriceForm((prev) => ({ ...prev, frequency }));
    }
  }, [planDetail]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    if (activeTab === "Addon" && name === "name") {
      // In Addon mode, name field is used for parent plan selection
      const selectedPlan = plans.find((p) => p._id === value) as any;
      const cycle = selectedPlan?.cycle === "MONTHLY" ? "Monthly" : "Annual";
      setFormData((prev) => ({
        ...prev,
        parentPlan: value,
        name: selectedPlan?.name ?? "",
        segment: selectedPlan?.segment?._id || selectedPlan?.segment || "",
        tier: selectedPlan?.tier?._id || selectedPlan?.tier || "",
        planType: selectedPlan?.type || "",
      }));
      setPriceForm((prev) => ({ ...prev, frequency: cycle }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleScreenCount = (increment: boolean) => {
    if (activeTab === "Addon") return; // Disabled for Addon

    setFormData((prev) => ({
      ...prev,
      noScreens: Math.max(2, prev.noScreens + (increment ? 1 : -1)),
    }));
  };

  const addPriceToList = () => {
    if (priceForm.price <= 0) {
      toast.error("Price must be greater than 0");
      return;
    }
    const newPrice: PlanPrice = {
      id: Date.now().toString(),
      ...priceForm,
    };
    setPriceList([...priceList, newPrice]);
    setPriceForm({ ...priceForm, price: 0 }); // Reset price input
  };

  const removePrice = (id: string) => {
    setPriceList(priceList.filter((p) => p.id !== id));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.segment || !formData.tier) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (priceList.length === 0) {
      toast.error("Please add at least one price to the list");
      return;
    }

    // Group prices by frequency
    const monthlyPrices = priceList.filter((p) => p.frequency === "Monthly");
    const annualPrices = priceList.filter((p) => p.frequency === "Annual");

    // Check for duplicate plans (skip when editing)
    const variant = activeTab === "License" ? "LICENSE" : "ADDON_SCREEN";
    const planType = formData.planType || ScreenType.BASIC;

    // Check for monthly plan duplicate (only when creating)
    if (!planId && monthlyPrices.length > 0) {
      const existingMonthly = plans.find(
        (p: any) =>
          ((p?.segment as any)?._id || p?.segment) === formData.segment &&
          ((p?.tier as any)?._id || p?.tier) === formData.tier &&
          p.type === planType &&
          p?.variant === variant &&
          p.cycle === "MONTHLY",
      );

      if (existingMonthly) {
        toast.error(
          "A plan with the same segment, tier, type, variant, and monthly cycle already exists",
        );
        return;
      }
    }

    // Check for annual plan duplicate (only when creating)
    if (!planId && annualPrices.length > 0) {
      const existingAnnual = plans.find(
        (p: any) =>
          ((p?.segment as any)?._id || p?.segment) === formData.segment &&
          ((p?.tier as any)?._id || p?.tier) === formData.tier &&
          p.type === planType &&
          p?.variant === variant &&
          p.cycle === "ANNUALLY",
      );

      if (existingAnnual) {
        toast.error(
          "A plan with the same segment, tier, type, variant, and annual cycle already exists",
        );
        return;
      }
    }

    try {
      if (planId) {
        // Edit mode - update existing plan
        const updatePayload: any = {
          name: formData.name,
          description: formData.description,
          segment: formData.segment,
          tier: formData.tier,
          type: formData.planType || ScreenType.BASIC,
          variant: activeTab === "License" ? "LICENSE" : "ADDON_SCREEN",
          screens: formData.noScreens,
          prices: priceList.map((p) => ({
            currency: p.currency,
            price: p.price,
          })),
        };

        if (activeTab === "Addon" && formData.parentPlan) {
          updatePayload.basePlan = formData.parentPlan;
        }

        await editPlan({ id: planId, body: updatePayload }).unwrap();
        navigate(adminRoutes.planList);
      } else {
        // Create mode - create new plan(s)
        const basePayload: any = {
          name: formData.name,
          description: formData.description,
          segment: formData.segment,
          tier: formData.tier,
          type: formData.planType || ScreenType.BASIC,
          variant: activeTab === "License" ? "LICENSE" : "ADDON_SCREEN",
          screens: formData.noScreens,
        };

        if (activeTab === "Addon" && formData.parentPlan) {
          basePayload.basePlan = formData.parentPlan;
        }

        const promises = [];

        if (monthlyPrices.length > 0) {
          promises.push(
            createPlan({
              ...basePayload,
              name: `${formData.name}`,
              cycle: "MONTHLY",
              prices: monthlyPrices.map((p) => ({
                currency: p.currency,
                price: p.price,
              })),
            }).unwrap(),
          );
        }

        if (annualPrices.length > 0) {
          promises.push(
            createPlan({
              ...basePayload,
              name: `${formData.name}`,
              cycle: "ANNUALLY",
              prices: annualPrices.map((p) => ({
                currency: p.currency,
                price: p.price,
              })),
            }).unwrap(),
          );
        }

        await Promise.all(promises);
        toast.success("Plans created successfully");
        navigate(adminRoutes.planList);
      }
    } catch (error) {
      console.error(
        planId ? "Failed to update plan" : "Failed to create plan",
        error,
      );
      // Error is already toasted by the RTK api onQueryStarted
    }
  };

  // Styles
  const pageBg = theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50";
  const cardBg = theme === "dark" ? "bg-[#1E1E1E]" : "bg-white";
  const borderColor = theme === "dark" ? "border-[#2C2C2C]" : "border-gray-200";
  const inputBg = theme === "dark" ? "bg-[#111111]" : "bg-white";
  const textColor = theme === "dark" ? "text-gray-200" : "text-gray-800";
  const labelColor = theme === "dark" ? "text-gray-400" : "text-gray-600";
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-16 pb-4 min-h-screen ${
        theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
      }`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {" "}
      {/* Header */}
      <div className="mb-4">
        <h1 className={`text-xl font-bold ${textColor}`}>Plan Overview</h1>
        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
          <span>Dashboard</span>
          <span>&gt;</span>
          <span className="text-[#B6CB24]">
            {planId ? "Edit License" : "Create License"}
          </span>
        </div>
      </div>
      {/* Main Card */}
      <div className={`rounded-xl border ${borderColor} ${cardBg} p-4`}>
        {/* Title Row */}
        <div className="flex justify-between items-start mb-2 border-b border-[#2C2C2C] pb-4">
          <h2 className={`text-lg font-semibold ${textColor}`}>
            {planId ? "Edit Plan / Addon" : "Create Plan / Addon"}
          </h2>
          <span className="text-xs text-[#B6CB24]">
            * Billing amount and billing frequency can not be changed later.
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Details */}
          <div className="space-y-4">
            {/* Create New Tabs */}
            <div>
              <label className={`block mb-2 font-medium ${labelColor}`}>
                Create New
              </label>
              <div className="inline-flex rounded-full p-1 bg-[#111111] border border-[#3A3A3A]">
                <button
                  type="button"
                  onClick={() => !planId && setActiveTab("License")}
                  disabled={!!planId}
                  className={`px-8 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeTab === "License"
                      ? "text-black bg-[radial-gradient(94.17%_100%_at_52.48%_0%,rgba(198,217,44,0.80)_0%,rgba(83,91,16,0.80)_100%)]"
                      : "text-gray-500 hover:text-gray-300"
                  } ${planId ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  License
                </button>
                <button
                  type="button"
                  onClick={() => !planId && setActiveTab("Addon")}
                  disabled={!!planId}
                  className={`px-8 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeTab === "Addon"
                      ? "text-black bg-[radial-gradient(94.17%_100%_at_52.48%_0%,rgba(198,217,44,0.80)_0%,rgba(83,91,16,0.80)_100%)]"
                      : "text-gray-500 hover:text-gray-300"
                  } ${planId ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  Addon
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Name / Plan Selection */}
              <div className="col-span-1">
                <label className={`block mb-2 ${labelColor}`}>
                  {activeTab === "License" ? "Licence Name" : "Choose Licence"}
                  <span className="text-red-500">*</span>
                </label>
                {activeTab === "License" ? (
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter here"
                    className={`w-full px-4 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:border-[#B6CB24]`}
                  />
                ) : (
                  <select
                    name="name"
                    value={formData.parentPlan}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:border-[#B6CB24] appearance-none`}
                  >
                    <option value="" disabled>
                      Choose from list
                    </option>
                    {plans.map((p: Plan) => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Choose Segment */}
              <div className="col-span-1">
                <label className={`block mb-2 ${labelColor}`}>
                  Choose Segment<span className="text-red-500">*</span>
                </label>
                <select
                  name="segment"
                  value={formData.segment}
                  onChange={handleInputChange}
                  disabled={activeTab === "Addon"}
                  className={`w-full px-4 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:border-[#B6CB24] appearance-none ${activeTab === "Addon" ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <option value="" disabled>
                    Choose from list
                  </option>
                  {segments.map((seg: ISegment) => (
                    <option key={seg._id} value={seg._id}>
                      {seg.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Choose Tier */}
              <div className="col-span-1">
                <label className={`block mb-2 ${labelColor}`}>
                  Choose Tier<span className="text-red-500">*</span>
                </label>
                <select
                  name="tier"
                  value={formData.tier}
                  onChange={handleInputChange}
                  disabled={activeTab === "Addon"}
                  className={`w-full px-4 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:border-[#B6CB24] appearance-none ${activeTab === "Addon" ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <option value="" disabled>
                    Choose from list
                  </option>
                  {tiers.map((t: ITier) => (
                    <option key={t._id} value={t._id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Choose Plan Type */}
              <div className="col-span-1">
                <label className={`block mb-2 ${labelColor}`}>
                  Choose Plan Type<span className="text-red-500">*</span>
                </label>
                <select
                  name="planType"
                  value={formData.planType}
                  onChange={handleInputChange}
                  disabled={activeTab === "Addon"}
                  className={`w-full px-4 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:border-[#B6CB24] appearance-none ${activeTab === "Addon" ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <option value="" disabled>
                    Choose from list
                  </option>
                  <option value={ScreenType.BASIC}>{ScreenType.BASIC}</option>
                  <option value={ScreenType.PREMIUM}>
                    {ScreenType.PREMIUM}
                  </option>
                </select>
              </div>
            </div>

            {/* No. Screens */}
            <div>
              <label className={`block mb-2 ${labelColor}`}>
                No. Screens<span className="text-red-500">*</span>
              </label>
              <div
                className={`flex items-center w-2/4 justify-between px-4 py-3 rounded-2xl border ${borderColor} ${inputBg}`}
              >
                <button
                  type="button"
                  onClick={() => handleScreenCount(false)}
                  disabled={activeTab === "Addon"}
                  className={`text-gray-400 transition-colors ${activeTab === "Addon" ? "opacity-30 cursor-not-allowed" : "hover:text-white"}`}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M8 12H16"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
                <input
                  type="number"
                  min={activeTab === "License" ? 2 : 1}
                  disabled={activeTab === "Addon"}
                  value={String(formData.noScreens).padStart(2, "0")}
                  onChange={(e) => {
                    if (activeTab === "Addon") return;
                    const value = Number(e.target.value);
                    const minVal = activeTab === "License" ? 2 : 1;
                    setFormData((prev) => ({
                      ...prev,
                      noScreens: Number.isNaN(value)
                        ? minVal
                        : Math.max(minVal, value),
                    }));
                  }}
                  className={`w-full text-center bg-transparent border-none focus:outline-none focus:ring-0 font-medium text-lg ${textColor} ${activeTab === "Addon" ? "opacity-60" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => handleScreenCount(true)}
                  disabled={activeTab === "Addon"}
                  className={`text-gray-400 transition-colors ${activeTab === "Addon" ? "opacity-30 cursor-not-allowed" : "hover:text-white"}`}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M8 12H16"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M12 16V8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className={`block mb-2 ${labelColor}`}>
                Enter Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter here"
                rows={4}
                className={`w-full px-4 py-3 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:border-[#B6CB24] resize-none`}
              />
            </div>
          </div>

          {/* Right Column: Price List */}
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              {/* Currency */}
              <div className="w-32">
                <label className={`block mb-2 ${labelColor}`}>
                  Currency<span className="text-red-500">*</span>
                </label>
                <select
                  value={priceForm.currency}
                  onChange={(e) =>
                    setPriceForm({ ...priceForm, currency: e.target.value })
                  }
                  className={`w-full px-3 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} text-sm focus:outline-none focus:border-[#B6CB24]`}
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="AED">AED</option>
                </select>
              </div>

              {/* Price */}
              <div className="w-24">
                <label className={`block mb-2 ${labelColor}`}>
                  Price<span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={priceForm.price}
                  onChange={(e) =>
                    setPriceForm({
                      ...priceForm,
                      price: Math.max(0, Number(e.target.value)),
                    })
                  }
                  className={`w-full px-3 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} text-sm focus:outline-none focus:border-[#B6CB24]`}
                />
              </div>

              {/* Cost by (Frequency) */}
              <div className="flex-1 min-w-[150px]">
                <label className={`block mb-2 ${labelColor}`}>
                  Cost by<span className="text-red-500">*</span>
                </label>
                  <div
                    className={`flex rounded-full border ${borderColor} ${inputBg} p-1`}
                  >
                    <button
                      disabled={
                        (!!planId && priceForm.frequency !== "Monthly") ||
                        (activeTab === "Addon" &&
                          !!formData.parentPlan &&
                          priceForm.frequency !== "Monthly")
                      }
                      onClick={() =>
                        setPriceForm({ ...priceForm, frequency: "Monthly" })
                      }
                      className={`flex-1 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        priceForm.frequency === "Monthly"
                          ? "bg-[#B6CB24] text-black"
                          : "text-gray-500 hover:text-gray-300"
                      } disabled:opacity-30 disabled:cursor-not-allowed`}
                    >
                      Monthly
                    </button>
                    <button
                      disabled={
                        (!!planId && priceForm.frequency !== "Annual") ||
                        (activeTab === "Addon" &&
                          !!formData.parentPlan &&
                          priceForm.frequency !== "Annual")
                      }
                      onClick={() =>
                        setPriceForm({ ...priceForm, frequency: "Annual" })
                      }
                      className={`flex-1 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        priceForm.frequency === "Annual"
                          ? "bg-[#B6CB24] text-black"
                          : "text-gray-500 hover:text-gray-300"
                      } disabled:opacity-30 disabled:cursor-not-allowed`}
                    >
                      Annual
                    </button>
                  </div>
              </div>

              {/* Add Button */}
              <button
                type="button"
                onClick={addPriceToList}
                className="px-4 py-2.5 rounded-lg bg-[radial-gradient(94.17%_100%_at_52.48%_0%,rgba(198,217,44,0.80)_0%,rgba(83,91,16,0.80)_100%)] border border-[#B6CB24] text-[#B6CB24] hover:bg-[#B6CB24]/10 text-sm font-medium flex items-center gap-1 mb-[1px]"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add to List
              </button>
            </div>

            {/* Price List Display */}
            <div>
              <label className={`block mb-2 ${labelColor}`}>Price List</label>
              <div
                className={`w-full h-72 rounded-xl border ${borderColor} ${inputBg} overflow-hidden flex flex-col`}
              >
                {/* Table Header */}
                <div
                  className={`grid grid-cols-5 px-6 py-4 border-b ${borderColor} text-xs font-semibold text-gray-400 uppercase tracking-wider`}
                >
                  <div>Cost By</div>
                  <div>Currency</div>
                  <div>Cost By</div>
                  <div>Price</div>
                  <div className="text-right">Action</div>
                </div>

                {/* Table Body */}
                <div className="flex-1 overflow-y-auto px-2 py-2">
                  {priceList.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                      <span className="text-sm">Add price to list</span>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {priceList.map((p) => (
                        <div
                          key={p.id}
                          className={`grid grid-cols-5 items-center px-4 py-3 rounded-lg hover:bg-white/5 transition-colors text-sm ${textColor}`}
                        >
                          <div className="text-gray-400">Monthly</div>
                          <div className="font-medium">{p.currency}</div>
                          <div className="text-gray-400">{p.frequency}</div>
                          <div className="font-medium">{p.price}</div>
                          <div className="flex justify-end">
                            <button
                              onClick={() => removePrice(p.id)}
                              className="p-1.5 rounded-md text-red-500 hover:bg-red-500/10 transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 6h18" />
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center gap-4 mt-8 pt-6 border-t border-[#2C2C2C]">
          <button
            onClick={handleSave}
            disabled={isLoading}
            className={`px-8 py-2.5 rounded-[10px] font-medium text-black transition-opacity ${
              isLoading ? "opacity-70" : "hover:opacity-90"
            }`}
            style={{
              background:
                "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            }}
          >
            {isLoading ? "Saving..." : planId ? "Update" : "Save"}
          </button>
          <button
            onClick={() => navigate("/plans")}
            className={`px-8 py-2.5 rounded-[10px] font-medium border ${borderColor} ${textColor} hover:bg-white/5 transition-colors`}
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanCreate;
